import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { PRIORITA, nowLocalDatetimeInput, isoToLocalInput, localToIso } from "@/lib/format";
import { REM } from "@/constants/testIds";
import api from "@/lib/api";
import { toast } from "sonner";

const EMPTY = {
  titolo: "",
  descrizione: "",
  scheduled_at_local: nowLocalDatetimeInput(),
  priorita: "media",
  client_id: "",
};

export default function ReminderDialog({ open, onOpenChange, initial, defaultClientId, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [clients, setClients] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        titolo: initial.titolo || "",
        descrizione: initial.descrizione || "",
        scheduled_at_local: isoToLocalInput(initial.scheduled_at) || nowLocalDatetimeInput(),
        priorita: initial.priorita || "media",
        client_id: initial.client_id || "",
      });
    } else {
      setForm({ ...EMPTY, scheduled_at_local: nowLocalDatetimeInput(), client_id: defaultClientId || "" });
    }
    api.get("/clients", { params: { limit: 2000 } }).then((r) => setClients(r.data)).catch(() => {});
  }, [open, initial, defaultClientId]);

  const isEdit = !!initial?.id;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.titolo.trim()) {
      toast.error("Il titolo è obbligatorio");
      return;
    }
    if (!form.scheduled_at_local) {
      toast.error("La data del richiamo è obbligatoria");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        titolo: form.titolo,
        descrizione: form.descrizione,
        scheduled_at: localToIso(form.scheduled_at_local),
        priorita: form.priorita,
        client_id: form.client_id || null,
      };
      let res;
      if (isEdit) {
        res = await api.put(`/reminders/${initial.id}`, payload);
        toast.success("Richiamo aggiornato");
      } else {
        res = await api.post("/reminders", payload);
        toast.success("Richiamo creato");
      }
      onOpenChange(false);
      window.dispatchEvent(new Event("crm:data-changed"));
      onSaved && onSaved(res.data);
    } catch {
      toast.error("Errore nel salvataggio del richiamo");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={REM.dialog} className="max-w-lg bg-white">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold tracking-tight">
            {isEdit ? "Modifica Richiamo" : "Nuovo Richiamo"}
          </DialogTitle>
          <DialogDescription className="text-stone-500">
            Programma un promemoria che apparirà nella dashboard.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Titolo *</Label>
            <Input
              data-testid={REM.fieldTitolo}
              value={form.titolo}
              onChange={(e) => set("titolo", e.target.value)}
              placeholder="Es. Richiamare per preventivo lampioni"
              className="mt-1.5"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Cliente Collegato</Label>
            <Select value={form.client_id || "__none__"} onValueChange={(v) => set("client_id", v === "__none__" ? "" : v)}>
              <SelectTrigger data-testid={REM.fieldClient} className="mt-1.5 bg-white">
                <SelectValue placeholder="Nessun cliente" />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                <SelectItem value="__none__">— Nessun cliente —</SelectItem>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.ragione_sociale}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Data & Ora *</Label>
              <Input
                data-testid={REM.fieldScheduled}
                type="datetime-local"
                value={form.scheduled_at_local}
                onChange={(e) => set("scheduled_at_local", e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Priorità</Label>
              <Select value={form.priorita} onValueChange={(v) => set("priorita", v)}>
                <SelectTrigger data-testid={REM.fieldPriorita} className="mt-1.5 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {PRIORITA.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Note</Label>
            <Textarea
              data-testid={REM.fieldDescrizione}
              value={form.descrizione}
              onChange={(e) => set("descrizione", e.target.value)}
              rows={3}
              placeholder="Dettagli, argomenti da discutere…"
              className="mt-1.5"
            />
          </div>
        </div>

        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button data-testid={REM.saveBtn} onClick={submit} disabled={saving}
            className="bg-[#F56A1F] hover:bg-[#E05206] text-white">
            {saving ? "Salvataggio…" : (isEdit ? "Salva" : "Crea Richiamo")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
